import { useCallback, useEffect, useRef, useState } from "react";

/** How long a copy button says "Copied" before it goes back to "Copy". */
export const COPIED_FLAG_MS = 2000;

/**
 * A copy button's "Copied" state, which turns itself off after
 * `COPIED_FLAG_MS`. The timer is cancelled when the component unmounts: a
 * dialog closed within two seconds of a copy otherwise set state on an
 * unmounted component, and in a test file that ended inside the window it
 * fired after jsdom was torn down, failing the run with
 * `window is not defined` (the `api-keys-card` test, 2026-10-05).
 */
export function useCopiedFlag(): [copied: boolean, markCopied: () => void] {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const markCopied = useCallback(() => {
    clearTimeout(timer.current);
    setCopied(true);
    timer.current = setTimeout(() => setCopied(false), COPIED_FLAG_MS);
  }, []);

  return [copied, markCopied];
}
